import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Save, Plus, Trash2, GripVertical, ChevronDown, ChevronRight, Eye, EyeOff, FolderTree, Loader2, Upload, ImageIcon, Grid3X3 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { AdminLayout } from "../components/admin/AdminSidebar";
import { AdminLoadingState, AdminApiError } from "../components/admin/AdminErrorBoundary";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCategories() {
  const [flat, setFlat] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) { navigate("/admin/login"); return; }
    fetchCategories();
  }, [navigate]);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.get(`${API}/settings/categories/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFlat(res.data?.flat || []);
      setFetchError(null);
      const exp = {};
      (res.data?.flat || []).filter(c => !c.parent_id).forEach(c => { exp[c.id] = true; });
      setExpanded(exp);
    } catch (err) {
      console.error("[AdminCategories] Failed to load:", err);
      setFetchError("Failed to load categories. Please refresh or contact support.");
    } finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("adminToken");
      await axios.put(`${API}/settings/categories`, { categories: flat }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Categories saved");
    } catch { toast.error("Failed to save"); } finally { setSaving(false); }
  };

  const addCategory = (parentId = null) => {
    const id = `cat-${Date.now()}`;
    const siblings = (flat || []).filter(c => c.parent_id === parentId);
    setFlat([...flat, {
      id, name: "New Category", slug: id, parent_id: parentId,
      order: siblings.length, enabled: true, image_url: "", label: "",
    }]);
    if (parentId) setExpanded(e => ({ ...e, [parentId]: true }));
  };

  const updateCategory = (id, field, value) => {
    setFlat((flat || []).map(c => c.id === id ? { ...c, [field]: value } : c));
  };

  const removeCategory = (id) => {
    const children = (flat || []).filter(c => c.parent_id === id);
    if (children.length > 0) {
      toast.error("Remove subcategories first");
      return;
    }
    setFlat((flat || []).filter(c => c.id !== id));
  };

  const moveUp = (id, parentId) => {
    const siblings = (flat || []).filter(c => c.parent_id === parentId).sort((a, b) => a.order - b.order);
    const idx = siblings.findIndex(c => c.id === id);
    if (idx <= 0) return;
    const updated = (flat || []).map(c => {
      if (c.id === siblings[idx].id) return { ...c, order: siblings[idx - 1].order };
      if (c.id === siblings[idx - 1].id) return { ...c, order: siblings[idx].order };
      return c;
    });
    setFlat(updated);
  };

  const moveParent = (id, newParentId) => {
    setFlat(flat.map(c => c.id === id ? { ...c, parent_id: newParentId || null } : c));
  };

  const handleImageUpload = async (e, catId) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const token = localStorage.getItem("adminToken");
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/upload/image`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      updateCategory(catId, "image_url", res.data.url);
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed");
    }
  };

  const topLevel = (flat || []).filter(c => !c.parent_id).sort((a, b) => a.order - b.order);

  if (loading) return <AdminLayout><AdminLoadingState message="Loading categories..." /></AdminLayout>;

  return (
    <AdminLayout>
      <div className="max-w-[900px] mx-auto px-4 lg:px-8 py-8 lg:py-12">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Link to="/admin/dashboard" className="text-[#676664]/40 hover:text-[#676664]"><ArrowLeft className="w-4 h-4" /></Link>
            <div className="flex items-center gap-2">
              <FolderTree className="w-4 h-4 text-[#9A7B4F]" />
              <h1 className="text-xl text-[#282828] font-light">Category Manager</h1>
            </div>
          </div>
          <div className="flex gap-2">
            <Link to="/admin/catalogue">
              <Button variant="outline" className="text-[11px] tracking-[0.1em] uppercase border-[#9A7B4F] text-[#9A7B4F] hover:bg-[#9A7B4F]/5" data-testid="catalogue-edit-btn">
                <Grid3X3 className="w-3.5 h-3.5 mr-1" /> Catalogue Edit
              </Button>
            </Link>
            <Button onClick={() => addCategory(null)} variant="outline" className="text-[11px] tracking-[0.1em] uppercase border-[#282828]/10" data-testid="add-top-category">
              <Plus className="w-3.5 h-3.5 mr-1" /> Top Category
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#282828] hover:bg-[#1a1a1a] text-white text-[11px] tracking-[0.12em] uppercase px-6" data-testid="save-categories-btn">
              <Save className="w-3.5 h-3.5 mr-2" /> {saving ? "Saving..." : "Save All"}
            </Button>
          </div>
        </div>

        {fetchError && <AdminApiError message={fetchError} onRetry={fetchCategories} />}

        <div className="space-y-2">
          {(topLevel || []).map((cat) => {
            const children = (flat || []).filter(c => c.parent_id === cat.id).sort((a, b) => a.order - b.order);
            const isExpanded = expanded[cat.id];
            return (
              <div key={cat.id} className="bg-white border border-[#282828]/8" data-testid={`category-${cat.id}`}>
                {/* Parent Row */}
                <div className="flex items-center gap-2 px-4 py-3">
                  <button onClick={() => moveUp(cat.id, null)} className="text-[#676664]/20 hover:text-[#676664]"><GripVertical className="w-3.5 h-3.5" /></button>
                  <button onClick={() => setExpanded(e => ({ ...e, [cat.id]: !isExpanded }))} className="text-[#676664]/40 hover:text-[#676664]">
                    {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                  <Input
                    value={cat.name}
                    onChange={(e) => updateCategory(cat.id, "name", e.target.value)}
                    className="flex-1 border-0 text-[13px] py-1 h-auto focus-visible:ring-0 font-medium"
                    data-testid={`cat-name-${cat.id}`}
                  />
                  <Input
                    value={cat.image_url}
                    onChange={(e) => updateCategory(cat.id, "image_url", e.target.value)}
                    placeholder="Image URL"
                    className="w-40 border-[#282828]/8 text-[11px] py-1 h-8"
                  />
                  <label className="cursor-pointer flex items-center gap-1 px-2 py-1 bg-[#FAF8F0] hover:bg-[#f0ede5] text-[#676664] text-[10px] border border-[#282828]/8" title="Upload image">
                    <Upload className="w-3 h-3" />
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, cat.id)} />
                  </label>
                  {cat.image_url && <div className="w-8 h-8 overflow-hidden bg-[#f7f5f4] border border-[#282828]/8 flex-shrink-0"><img src={cat.image_url} alt="" className="w-full h-full object-cover" /></div>}
                  <button onClick={() => updateCategory(cat.id, "enabled", !cat.enabled)} className={cat.enabled ? "text-[#4A7C59]" : "text-[#676664]/20"} title={cat.enabled ? "Active" : "Inactive"} data-testid={`toggle-${cat.id}`}>
                    {cat.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button onClick={() => removeCategory(cat.id)} className="text-[#676664]/20 hover:text-red-500" data-testid={`delete-${cat.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  <span className="text-[9px] text-[#676664]/30 ml-1">{children.length} sub</span>
                </div>

                {/* Children */}
                {isExpanded && (
                  <div className="border-t border-[#282828]/5 bg-[#FAF8F0]/50">
                    {children.map((sub) => (
                      <div key={sub.id} className="flex items-center gap-2 px-4 py-2.5 ml-8 border-b border-[#282828]/3 last:border-0" data-testid={`subcategory-${sub.id}`}>
                        <button onClick={() => moveUp(sub.id, cat.id)} className="text-[#676664]/15 hover:text-[#676664]"><GripVertical className="w-3 h-3" /></button>
                        <Input
                          value={sub.name}
                          onChange={(e) => updateCategory(sub.id, "name", e.target.value)}
                          className="flex-1 border-0 text-[12px] py-0.5 h-auto focus-visible:ring-0"
                          data-testid={`sub-name-${sub.id}`}
                        />
                        <select
                          value={sub.parent_id || ""}
                          onChange={(e) => moveParent(sub.id, e.target.value)}
                          className="text-[10px] text-[#676664]/60 border border-[#282828]/8 rounded px-2 py-1 bg-white"
                        >
                          {(topLevel || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                        <Input
                          value={sub.image_url || ""}
                          onChange={(e) => updateCategory(sub.id, "image_url", e.target.value)}
                          placeholder="Image URL"
                          className="w-32 border-[#282828]/8 text-[10px] py-0.5 h-7"
                        />
                        <label className="cursor-pointer flex items-center px-1.5 py-1 bg-[#FAF8F0] hover:bg-[#f0ede5] text-[#676664] border border-[#282828]/8" title="Upload image">
                          <Upload className="w-3 h-3" />
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, sub.id)} />
                        </label>
                        {sub.image_url && <div className="w-7 h-7 overflow-hidden bg-[#f7f5f4] border border-[#282828]/8 flex-shrink-0"><img src={sub.image_url} alt="" className="w-full h-full object-cover" /></div>}
                        <button onClick={() => updateCategory(sub.id, "enabled", !sub.enabled)} className={sub.enabled ? "text-[#4A7C59]" : "text-[#676664]/20"}>
                          {sub.enabled ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                        </button>
                        <button onClick={() => removeCategory(sub.id)} className="text-[#676664]/15 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                      </div>
                    ))}
                    <button onClick={() => addCategory(cat.id)} className="flex items-center gap-1.5 text-[10px] text-[#9A7B4F] px-4 py-2.5 ml-8 hover:bg-[#9A7B4F]/5 transition-colors w-full" data-testid={`add-sub-${cat.id}`}>
                      <Plus className="w-3 h-3" /> Add Subcategory
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {topLevel.length === 0 && (
          <div className="text-center py-16 bg-white border border-[#282828]/8">
            <FolderTree className="w-10 h-10 text-[#676664]/20 mx-auto mb-4" strokeWidth={1} />
            <p className="text-[#676664]/50 text-sm font-light">No categories yet</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
